import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import cx from 'classnames';

import { formatPrice } from 'utilities/money';

import { GreenButton } from '@marketing/components/buttons';
import SectionHeader from '@marketing/components/section_header';

import BannerCheckIcon from './assets/banner_check_icon.svg';
import BubbleQuestionIcon from './assets/bubble_question_icon.svg';
import ChecklistIcon from './assets/checklist_icon.svg';
import CheckmarkIcon from '../../assets/checkmark_icon.svg';
import CircleRefreshIcon from './assets/circle_refresh_icon.svg';
import CircleChevronIcon from './assets/circle_chevron_icon.svg';
import DollarSignIcon from './assets/dollar_sign_icon.svg';
import FireworkIcon from './assets/firework_icon.svg';
import FlashcardsIcon from './assets/flashcards_icon.svg';
import HighFiveIcon from './assets/high_five_icon.svg';
import HourglassIcon from './assets/hourglass_icon.svg';
import MagicWandIcon from './assets/magic_wand_icon.svg';
import MagooshLogo from '@magoosh/assets/magoosh-logo-purple.png';
import MobileDeviceIcon from './assets/mobile_device_icon.svg';
import NotebookIcon from './assets/notebook_icon.svg';
import ScoreGaugeIcon from './assets/score_gauge_icon.svg';
import StarIcon from './assets/star_icon.svg';
import VideoPlayerIcon from './assets/video_player_icon.svg';
import styles from './style.module.scss';

interface Props {
  admissionsBundleOfferFullPriceInCents: number;
  admissionsBundleOfferRegistrationUrl: string;
}

const CompetitorComparisonSection: React.FC<Props> = (props) => {
  const options = ['Magoosh', 'Princeton Review', 'Kaplan', 'Manhattan'];

  const tableRef = useRef(null);
  const magooshColumnRef = useRef(null);
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [gradientBgDimensions, setGradientBgDimensions] = useState(null);

  const determineGradientBgDimensions = useRef(() => {
    setGradientBgDimensions({
      height: tableRef.current.getBoundingClientRect().height,
      left: magooshColumnRef.current.getBoundingClientRect().left - tableRef.current.getBoundingClientRect().left - 10,
      top: 0,
      width: magooshColumnRef.current.getBoundingClientRect().width
    });
  });

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);

    window.addEventListener('resize', () => {
      if (window.innerWidth < 768 && !isMobile) {
        setIsMobile(true);
      } else if (window.innerWidth > 768 && isMobile) {
        setIsMobile(false);
      }
    });
  });

  useLayoutEffect(() => {
    determineGradientBgDimensions.current();
    window.addEventListener('resize', determineGradientBgDimensions.current);

    return () => {
      window.removeEventListener('resize', determineGradientBgDimensions.current);
    };
  }, []);

  const advanceToNextOption = () => {
    if (currentOptionIndex === options.length - 1) return;

    setCurrentOptionIndex(currentOptionIndex + 1);
  };

  const currentOption = () => {
    return options[currentOptionIndex];
  };

  const returnToPreviousOption = () => {
    if (currentOptionIndex === 0) return;

    setCurrentOptionIndex(currentOptionIndex - 1);
  };

  const Checkmark = (
    <img
      alt="Checkmark icon"
      height="16px"
      loading="lazy"
      src={CheckmarkIcon}
      style={{ height: 16, marginTop: 2, width: 14 }}
      width="14px"
    />
  );

  const Included = (
    <div className="d-flex gap-xs">
      {Checkmark}
      <div className="small text-inter">Included</div>
    </div>
  );

  const NotAvailable = (
    <div className="small text-inter" style={{ color: '#B2B2B2' }}>-</div>
  );

  return (
    <div className="d-flex flex-column gap-m">
      <SectionHeader
        className="u-margin-B-m"
        subtitle="See for yourself how Magoosh stacks up with others in the field."
        supertitle="Competitive advantage"
        supertitleIcon={MagicWandIcon}
        title="How Magoosh stacks up against other options"
      />

      <div className="visible-xs visible-sm">
        <div className="align-items-center d-flex gap-s">
          <div className="flex-none">
            <img
              alt="Left arrow icon"
              onClick={returnToPreviousOption}
              src={CircleChevronIcon}
              style={{ opacity: currentOptionIndex === 0 ? 0.3 : 1 }}
            />
          </div>

          <div className="flex-1 text-center">
            {
              currentOption() === 'Magoosh' ? (
                <div style={{ height: 28 }}>
                  <img alt="Magoosh logo" height="28px" src={MagooshLogo} width="121px" />
                </div>
              ) : (
                <div
                  className="text-bold text-inter"
                  style={{ fontSize: 18, height: 28 }}
                >
                  {options[currentOptionIndex]}
                </div>
              )
            }
          </div>

          <div className="flex-none">
            <img
              alt="Right arrow icon"
              onClick={advanceToNextOption}
              src={CircleChevronIcon}
              style={{
                opacity: currentOptionIndex === options.length - 1 ? 0.3 : 1,
                transform: 'scaleX(-1)'
              }}
            />
          </div>
        </div>
      </div>

      <div style={{ position: 'relative' }}>
        {
          currentOption() === 'Magoosh' && gradientBgDimensions  && (
            <div
              style={{
                background: 'linear-gradient(to bottom, transparent, #D4F0E5)',
                borderRadius: '0 0 22px 22px',
                position: 'absolute',
                ...gradientBgDimensions
              }}
            >
            </div>
          )
        }

        <table
          className={cx(styles.featureComparisonTable, `active-option-${currentOptionIndex}`)}
          ref={tableRef}
        >
          <thead>
            <th className="hidden-xs hidden-sm">
              <div className="text-inter u-margin-B-m" style={{ fontSize: 18, fontWeight: 400, height: 28 }}>
                Features breakdown
              </div>
            </th>

            <th className="hidden-xs hidden-sm">
              <div className="u-margin-B-m" style={{ height: 28 }}>
                <img alt="Magoosh logo" height="28px" src={MagooshLogo} width="121px" />
              </div>
            </th>

            <th className="hidden-xs hidden-sm">
              <div className="text-inter u-margin-B-m" style={{ fontSize: 18, height: 28 }}>
                Princeton Review
              </div>
            </th>

            <th className="hidden-xs hidden-sm">
              <div className="text-inter u-margin-B-m" style={{ fontSize: 18, height: 28 }}>
                Kaplan
              </div>
            </th>

            <th className="hidden-xs hidden-sm">
              <div className="text-inter u-margin-B-m" style={{ fontSize: 18, height: 28 }}>
                Manhattan
              </div>
            </th>
          </thead>

          <tbody>
            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Dollar sign icon" height="16px" loading="lazy" src={DollarSignIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Price</div>
                </div>
              </td>

              <td ref={magooshColumnRef}>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-bold text-inter">
                    {formatPrice(props.admissionsBundleOfferFullPriceInCents, 'usd', true)}
                  </div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-inter">$499</div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-inter">$499</div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-inter">$599</div>
                </div>
              </td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Hourglass icon" height="16px" loading="lazy" src={HourglassIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Length of Online Access</div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-bold text-inter">6 months</div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  <div className="small text-inter">4 months</div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-inter">6 months</div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-inter">6 months</div>
                </div>
              </td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Banner check icon" height="16px" loading="lazy" src={BannerCheckIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Official Content</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>
              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Bubble question icon" height="16px" loading="lazy" src={BubbleQuestionIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Practice Questions and Exams</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{Included}</td>
              <td>{Included}</td>
              <td>{Included}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Mobile device icon" height="16px" loading="lazy" src={MobileDeviceIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Mobile App</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{NotAvailable}</td>
              <td>{Included}</td>
              <td>{Included}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Firework icon" height="16px" loading="lazy" src={FireworkIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Free Trial</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
              <td>{Included}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Video play icon" height="16px" loading="lazy" src={VideoPlayerIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Video Lessons</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{Included}</td>
              <td>{Included}</td>
              <td>{Included}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Notebook icon" height="16px" loading="lazy" src={NotebookIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Video & Text Question Explanations</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Circle refresh icon" height="16px" loading="lazy" src={CircleRefreshIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter u-padding-R-s">No Questions Asked Money-Back Guarantee</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="High five icon" height="16px" loading="lazy" src={HighFiveIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Score-Improvement Guarantee</div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-bold text-inter">+5 Point Increase Guarantee</div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-inter">+1 Point Increase Guarantee</div>
                </div>
              </td>

              <td>
                <div className="d-flex gap-xs">
                  {Checkmark}
                  <div className="small text-inter">+1 Point Increase Guarantee</div>
                </div>
              </td>

              <td>{NotAvailable}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Checklist icon" height="16px" loading="lazy" src={ChecklistIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Study Plans</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{NotAvailable}</td>
              <td>{Included}</td>
              <td>{NotAvailable}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Star icon" height="16px" loading="lazy" src={StarIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Graduate Application Tracker & Resources</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Flashcards icon" height="16px" loading="lazy" src={FlashcardsIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Flashcards Included</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{NotAvailable}</td>
              <td>{Included}</td>
              <td>{NotAvailable}</td>
            </tr>

            <tr>
              <td>
                <div className="d-flex gap-xs">
                  <img alt="Score gauge icon" height="16px" loading="lazy" src={ScoreGaugeIcon} style={{ height: 16, marginTop: 2, width: 14 }} width="14px" />
                  <div className="small text-inter">Score Predictor</div>
                </div>
              </td>

              <td className="text-bold">{Included}</td>

              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
              <td>{NotAvailable}</td>
            </tr>

            <tr>
              <td />

              <td>
                <div className="d-flex gap-xs">
                  <GreenButton
                    block
                    href={props.admissionsBundleOfferRegistrationUrl}
                    style={{ width: 'calc(100% - 20px)' }}
                  >
                    Sign Up Now
                  </GreenButton>
                </div>
              </td>

              <td />
              <td />
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompetitorComparisonSection;
